// =============================================
// P0.1 — Offline Workout Draft Buffer
// Lightweight localStorage write-through buffer that protects logged sets
// from gym WiFi drops / Safari backgrounding. NOT a PWA / service worker /
// sync engine — just a single most-recent snapshot per workout, flushed to
// the backend on reconnect / visibility / pagehide and recovered on mount.
// =============================================

const PREFIX = 'workout_draft_';

const keyFor = (workoutId) => `${PREFIX}${workoutId}`;

// Persist the latest execution snapshot for a workout. Always written as
// unsynced — a confirmed backend save flips this via markSynced().
export const saveDraft = (workoutId, exercises) => {
  if (!workoutId) return;
  try {
    const existing = getDraft(workoutId);
    const draft = {
      workoutId,
      exercises,
      synced: false,
      updatedAt: Date.now(),
      syncedAt: existing?.syncedAt || 0,
    };
    localStorage.setItem(keyFor(workoutId), JSON.stringify(draft));
  } catch (e) {
    // localStorage unavailable / quota exceeded — non-blocking
  }
};

export const getDraft = (workoutId) => {
  if (!workoutId) return null;
  try {
    const raw = localStorage.getItem(keyFor(workoutId));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// Mark the current draft as confirmed-saved (records syncedAt so stale
// drafts never resurface after a successful synchronization).
export const markSynced = (workoutId) => {
  if (!workoutId) return;
  try {
    const draft = getDraft(workoutId);
    if (draft) {
      draft.synced = true;
      draft.syncedAt = Date.now();
      localStorage.setItem(keyFor(workoutId), JSON.stringify(draft));
    }
  } catch (e) {
    // non-blocking
  }
};

export const clearDraft = (workoutId) => {
  if (!workoutId) return;
  try {
    localStorage.removeItem(keyFor(workoutId));
  } catch (e) {
    // non-blocking
  }
};

// =============================================
// Durable workout START ANCHOR
// The elapsed-time timer is anchored to a start instant. Storing it only in
// component state means it resets to "now" whenever the component remounts —
// e.g. Android/PWA kills a backgrounded tab and the client reopens the app,
// which reset the timer to 0 and saved a 0-minute workout. Persisting the
// anchor keeps the elapsed time correct across kills / reloads / remounts.
const ANCHOR_PREFIX = 'workout_start_';
const anchorKey = (workoutId) => `${ANCHOR_PREFIX}${workoutId}`;

export const getStartAnchor = (workoutId) => {
  if (!workoutId) return null;
  try {
    return localStorage.getItem(anchorKey(workoutId)) || null;
  } catch (e) {
    return null;
  }
};

// Write once — never overwrite an existing anchor for the same workout.
export const saveStartAnchor = (workoutId, iso) => {
  if (!workoutId || !iso) return;
  try {
    if (!localStorage.getItem(anchorKey(workoutId))) {
      localStorage.setItem(anchorKey(workoutId), iso);
    }
  } catch (e) {
    // non-blocking
  }
};

export const clearStartAnchor = (workoutId) => {
  if (!workoutId) return;
  try {
    localStorage.removeItem(anchorKey(workoutId));
  } catch (e) {
    // non-blocking
  }
};

// A draft is recoverable only when it was never confirmed-saved AND it has
// changes newer than the last confirmed save. This guard prevents stale
// drafts from resurfacing after a workout has already been synchronized.
export const isDraftRecoverable = (draft) => {
  if (!draft || draft.synced !== false) return false;
  return draft.updatedAt > (draft.syncedAt || 0);
};
